import { DestroyRef, effect, inject, Service, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationDto, NotificationId } from '@workspace/data-types';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, Subject, tap } from 'rxjs';
import { AuthStore } from '../ngrx';

@Service()
export class NotificationStateService {
  private readonly api = inject(ApiService);
  private readonly authStore = inject(AuthStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly notifications = signal<NotificationDto[]>([]);
  readonly unreadCount = signal<number>(0);

  private readonly chatMessageReceived$ = new Subject<Record<string, unknown>>();
  public readonly chatMessageReceived = this.chatMessageReceived$.asObservable();

  private eventSource: EventSource | null = null;

  constructor() {
    effect(() => {
      const isAuth = this.authStore.isUserAuthenticated();
      if (isAuth) {
        this.initializeNotifications();
        this.connectSse();
      } else {
        this.disconnectSse();
        this.notifications.set([]);
        this.unreadCount.set(0);
      }
    });
  }

  private initializeNotifications(): void {
    this.api
      .notifications()
      .getNotifications(20, 0)
      .pipe(
        tap((list) => this.notifications.set(list)),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.api
      .notifications()
      .getUnreadCount()
      .pipe(
        tap((count) => this.unreadCount.set(count)),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private connectSse(): void {
    if (this.eventSource) {
      this.disconnectSse();
    }

    this.api
      .notifications()
      .getOneTimeToken()
      .pipe(
        tap(({ token }) => {
          const sseUrl = this.api.notifications().getSseUrl(token);
          this.eventSource = new EventSource(sseUrl, { withCredentials: true });

          this.eventSource.addEventListener('notification', (event: MessageEvent) => {
            try {
              const notification = JSON.parse(event.data) as NotificationDto;
              this.handleIncomingNotification(notification);
            } catch (err) {
              console.error('Failed to parse incoming notification event', err);
            }
          });

          this.eventSource.onerror = (err) => {
            console.error('SSE connection error:', err);
          };
        }),
        catchError((err) => {
          console.error('Failed to retrieve SSE One-Time Token', err);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private disconnectSse(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  private handleIncomingNotification(notification: NotificationDto): void {
    console.log('handleIncomingNotification called with:', notification);
    if (
      notification.metadata?.['type'] === 'chat_message' ||
      notification.metadata?.['type'] === 'chat_message_deleted'
    ) {
      console.log('chat_message/deleted notification detected, emitting metadata:', notification.metadata);
      this.chatMessageReceived$.next(notification.metadata);
      return;
    }

    this.notifications.update((list) => {
      if (list.some((n) => n._id === notification._id)) {
        return list;
      }
      return [notification, ...list];
    });

    if (!notification.read) {
      this.unreadCount.update((count) => count + 1);
    }
  }

  markAsRead(id: NotificationId): void {
    this.api
      .notifications()
      .markAsRead(id)
      .pipe(
        tap((updated) => {
          this.notifications.update((list) => list.map((n) => (n._id === updated._id ? updated : n)));
          this.unreadCount.update((count) => Math.max(0, count - 1));
        }),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  markAllAsRead(): void {
    this.api
      .notifications()
      .markAllAsRead()
      .pipe(
        tap(() => {
          this.notifications.update((list) => list.map((n) => ({ ...n, read: true })));
          this.unreadCount.set(0);
        }),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
